import React, { useState, useMemo } from 'react';
import { Search, Edit2, Save, Users, Download, Trash2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useRegistrations, useSchools, useEvents, useRegistrationMutations } from '../../hooks/useConvex';
import { useAuth } from '../../contexts/AuthContext';
import { Registration, Student, School as SchoolType, Event } from '../../types';
import { Id } from '../../../convex/_generated/dataModel';

export const ParticipantsPage: React.FC = () => {
    const { user, adminRole, assignedEvents, assignedSchoolId } = useAuth();
    const registrationsData = useRegistrations();
    const schoolsData = useSchools();
    const eventsData = useEvents();
    const { update } = useRegistrationMutations();
    const loading = registrationsData === undefined || schoolsData === undefined || eventsData === undefined;

    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'by_school' | 'by_event'>('by_school');

    // Edit State
    const [editingStudent, setEditingStudent] = useState<{
        registrationId: string;
        studentIndex: number;
        student: Student;
    } | null>(null);
    const [editReason, setEditReason] = useState('');
    const [saving, setSaving] = useState(false);

    const isEventManager = adminRole === 'event_manager';
    const isEscort = adminRole === 'escort';
    const isChief = user?.email === 'chaitanyamishra.ai@gmail.com';
    const hasEditPermission = adminRole === 'supreme_admin' || adminRole === 'admin' || adminRole === 'principal' || adminRole === 'school_admin_office' || isEventManager || isEscort;

    const canEditStudent = (student: Student, schoolId?: string) => {
        if (adminRole === 'supreme_admin' || adminRole === 'admin' || adminRole === 'principal' || adminRole === 'school_admin_office') return true;
        if (isEventManager) {
            return student.events.some(eventId => assignedEvents.includes(eventId));
        }
        if (isEscort && schoolId) {
            return schoolId === assignedSchoolId;
        }
        return false;
    };

    // Transform Convex data
    const registrations = useMemo(() => {
        if (!registrationsData) return [];
        let transformed = registrationsData.map((r: Record<string, unknown>) => ({
            ...r,
            registration_id: r._id,
        })) as Registration[];

        if (adminRole === 'escort' && assignedSchoolId) {
            transformed = transformed.filter((r: Registration) => r.school_id === assignedSchoolId);
        }

        return transformed;
    }, [registrationsData, adminRole, assignedSchoolId]);

    const schools = useMemo(() => {
        if (!schoolsData) return {} as Record<string, SchoolType>;
        return schoolsData.reduce((acc: Record<string, SchoolType>, school: Record<string, unknown>) => {
            acc[school._id as string] = { ...school, school_id: school._id } as SchoolType;
            return acc;
        }, {} as Record<string, SchoolType>);
    }, [schoolsData]);

    const events = useMemo(() => {
        if (!eventsData) return {} as Record<string, Event>;
        return eventsData.reduce((acc: Record<string, Event>, event: Record<string, unknown>) => {
            acc[event._id as string] = { ...event, event_id: event._id } as Event;
            return acc;
        }, {} as Record<string, Event>);
    }, [eventsData]);

    const handleSaveEdit = async () => {
        if (!editingStudent || !editReason.trim()) return;

        try {
            setSaving(true);
            const registration = registrations.find(r => r.registration_id === editingStudent.registrationId);
            if (!registration) throw new Error('Registration not found');

            const updatedStudents = [...registration.students];
            updatedStudents[editingStudent.studentIndex] = editingStudent.student;

            await update({
                registrationId: editingStudent.registrationId as Id<'registrations'>,
                students: updatedStudents,
            });

            setEditingStudent(null);
            setEditReason('');
        } catch (error) {
            console.error('Error updating student:', error);
            alert('Failed to update student details');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteStudent = async (registrationId: string, studentIndex: number, studentName: string) => {
        if (!window.confirm(`Are you sure you want to DELETE participant "${studentName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setSaving(true);
            const registration = registrations.find(r => r.registration_id === registrationId);
            if (!registration) throw new Error('Registration not found');

            const updatedStudents = registration.students.filter((_, idx) => idx !== studentIndex);

            await update({
                registrationId: registrationId as Id<'registrations'>,
                students: updatedStudents,
                totalParticipants: updatedStudents.length,
            });
        } catch (error: any) {
            console.error('Error deleting student:', error);
            alert(error.message || 'Failed to delete student');
        } finally {
            setSaving(false);
        }
    };

    const filteredRegistrations = useMemo(() => {
        return registrations.filter(reg => {
            const schoolName = schools[reg.school_id]?.school_name.toLowerCase() || '';
            return schoolName.includes(searchTerm.toLowerCase()) ||
                reg.students.some(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
        });
    }, [registrations, schools, searchTerm]);

    // Group students by school
    const studentsBySchool = filteredRegistrations.reduce((acc, reg) => {
        const schoolName = schools[reg.school_id]?.school_name || 'Unknown School';
        if (!acc[schoolName]) acc[schoolName] = [];
        reg.students.forEach((student, index) => {
            acc[schoolName].push({ ...student, registrationId: reg.registration_id, originalIndex: index, schoolId: reg.school_id });
        });
        return acc;
    }, {} as Record<string, (Student & { registrationId: string; originalIndex: number; schoolId: string })[]>);

    // Group students by event
    const studentsByEvent = filteredRegistrations.reduce((acc, reg) => {
        reg.students.forEach((student, index) => {
            student.events.forEach(eventId => {
                const eventName = events[eventId]?.name || eventId;
                if (!acc[eventName]) acc[eventName] = [];
                acc[eventName].push({
                    ...student,
                    registrationId: reg.registration_id,
                    originalIndex: index,
                    schoolName: schools[reg.school_id]?.school_name,
                    schoolId: reg.school_id
                });
            });
        });
        return acc;
    }, {} as Record<string, (Student & { registrationId: string; originalIndex: number; schoolName?: string; schoolId: string })[]>);

    const handleExportPDF = () => {
        if (activeTab === 'by_school') {
            const data: string[][] = [];
            Object.entries(studentsBySchool).forEach(([schoolName, students]) => {
                data.push([`--- ${schoolName} (${students.length} participants) ---`, '', '', '']);
                students.forEach(student => {
                    data.push([
                        student.name,
                        schoolName,
                        student.grade,
                        student.events.map(eid => events[eid]?.name || eid).join(', ')
                    ]);
                });
            });

            pdfService.generatePDF({
                title: 'Participants List (By School)',
                filename: 'participants_by_school',
                columns: ['Name', 'School', 'Grade', 'Events'],
                data: data,
                orientation: 'landscape',
            });
        } else {
            const data: string[][] = [];
            Object.entries(studentsByEvent).forEach(([eventName, students]) => {
                data.push([`--- ${eventName} (${students.length} participants) ---`, '', '', '']);
                students.forEach(student => {
                    data.push([
                        student.name,
                        student.schoolName || 'Unknown',
                        student.grade,
                        eventName
                    ]);
                });
            });

            pdfService.generatePDF({
                title: 'Participants List (By Event)',
                filename: 'participants_by_event',
                columns: ['Name', 'School', 'Grade', 'Event'],
                data: data,
                orientation: 'landscape',
            });
        }
    };

    const totalParticipants = registrations.reduce((sum, reg) => sum + reg.students.length, 0);

    if (loading) return <Loader size="lg" className="mx-auto mt-20" fullScreen />;

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Users className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Participants</h1>
                            <p className="text-sm text-white/30 mt-0.5">{totalParticipants} registered participants</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="secondary" onClick={handleExportPDF}>
                            <Download className="w-4 h-4 mr-2" />
                            Export
                        </Button>
                    </div>
                </div>
            </div>

            {/* Tab Bar */}
            <div className="flex gap-1 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1 w-fit">
                <button
                    onClick={() => setActiveTab('by_school')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                        activeTab === 'by_school'
                            ? 'bg-white/[0.06] text-white'
                            : 'text-white/35 hover:text-white/60'
                    }`}
                >
                    By School
                </button>
                <button
                    onClick={() => setActiveTab('by_event')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                        activeTab === 'by_event'
                            ? 'bg-white/[0.06] text-white'
                            : 'text-white/35 hover:text-white/60'
                    }`}
                >
                    By Event
                </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 w-4 h-4" />
                <input
                    type="text"
                    placeholder="Search participants or schools..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-white text-sm placeholder:text-white/25 focus:border-brand/30 focus:outline-none transition-all"
                />
            </div>

            {/* Content */}
            <div className="space-y-3">
                {activeTab === 'by_school' ? (
                    Object.entries(studentsBySchool).map(([schoolName, students]) => (
                        <Card key={schoolName} className="overflow-hidden">
                            <div className="px-5 py-3 bg-white/[0.02] border-b border-white/[0.04] flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white/60">
                                    {schoolName}
                                    {students[0]?.schoolId && schools[students[0].schoolId]?.team_code && (
                                        <span className="text-white/30 ml-2 font-mono text-xs">
                                            {schools[students[0].schoolId]?.team_code}
                                        </span>
                                    )}
                                </h3>
                                <Badge variant="neutral" size="sm">{students.length}</Badge>
                            </div>

                            {/* Desktop Table */}
                            <div className="overflow-x-auto hidden md:block">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/[0.04]">
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">#</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Name</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Grade</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Contact</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Events</th>
                                            {hasEditPermission && (
                                                <th className="px-5 py-3 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map((student, idx) => (
                                            <tr
                                                key={`${student.registrationId}-${idx}`}
                                                className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors duration-150"
                                            >
                                                <td className="px-5 py-3.5 text-xs text-white/30 font-mono">{idx + 1}</td>
                                                <td className="px-5 py-3.5 text-sm text-white font-medium">{student.name}</td>
                                                <td className="px-5 py-3.5 text-sm text-white/40">{student.grade}</td>
                                                <td className="px-5 py-3.5 text-sm text-white/40">{student.contact || '—'}</td>
                                                <td className="px-5 py-3.5">
                                                    <div className="flex flex-wrap gap-1">
                                                        {student.events.map(eid => (
                                                            <span
                                                                key={eid}
                                                                className="px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-[10px] text-white/50"
                                                            >
                                                                {events[eid]?.name || eid}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                {hasEditPermission && (
                                                    <td className="px-5 py-3.5 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {canEditStudent(student, student.schoolId) && (
                                                                <button
                                                                    onClick={() => setEditingStudent({
                                                                        registrationId: student.registrationId,
                                                                        studentIndex: student.originalIndex,
                                                                        student: { ...student }
                                                                    })}
                                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                                >
                                                                    <Edit2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                            {isChief && (
                                                                <button
                                                                    onClick={() => handleDeleteStudent(
                                                                        student.registrationId,
                                                                        student.originalIndex,
                                                                        student.name
                                                                    )}
                                                                    disabled={saving}
                                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="md:hidden divide-y divide-white/[0.04]">
                                {students.map((student, idx) => (
                                    <div key={`${student.registrationId}-${idx}`} className="p-4 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs text-white/30 font-mono">#{idx + 1}</span>
                                                    <p className="text-white font-medium">{student.name}</p>
                                                </div>
                                                <p className="text-sm text-white/40">Grade {student.grade}</p>
                                                {student.contact && (
                                                    <p className="text-xs text-white/30 mt-1">{student.contact}</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {hasEditPermission && canEditStudent(student, student.schoolId) && (
                                                    <button
                                                        onClick={() => setEditingStudent({
                                                            registrationId: student.registrationId,
                                                            studentIndex: student.originalIndex,
                                                            student: { ...student }
                                                        })}
                                                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06]"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {isChief && (
                                                    <button
                                                        onClick={() => handleDeleteStudent(
                                                            student.registrationId,
                                                            student.originalIndex,
                                                            student.name
                                                        )}
                                                        disabled={saving}
                                                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {student.events.map(eid => (
                                                <span
                                                    key={eid}
                                                    className="px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-[10px] text-white/50"
                                                >
                                                    {events[eid]?.name || eid}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))
                ) : (
                    Object.entries(studentsByEvent).map(([eventName, students]) => (
                        <Card key={eventName} className="overflow-hidden">
                            <div className="px-5 py-3 bg-white/[0.02] border-b border-white/[0.04] flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white/60">{eventName}</h3>
                                <Badge variant="neutral" size="sm">{students.length}</Badge>
                            </div>

                            {/* Desktop Table */}
                            <div className="overflow-x-auto hidden md:block">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/[0.04]">
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">#</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Name</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">School</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Grade</th>
                                            <th className="px-5 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Contact</th>
                                            {hasEditPermission && (
                                                <th className="px-5 py-3 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map((student, idx) => (
                                            <tr
                                                key={`${student.registrationId}-${idx}`}
                                                className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors duration-150"
                                            >
                                                <td className="px-5 py-3.5 text-xs text-white/30 font-mono">{idx + 1}</td>
                                                <td className="px-5 py-3.5 text-sm text-white font-medium">{student.name}</td>
                                                <td className="px-5 py-3.5 text-sm text-white/40">
                                                    {student.schoolName}
                                                    {schools[student.schoolId]?.team_code && (
                                                        <span className="text-white/20 ml-2 font-mono text-xs">
                                                            ({schools[student.schoolId]?.team_code})
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3.5 text-sm text-white/40">{student.grade}</td>
                                                <td className="px-5 py-3.5 text-sm text-white/40">{student.contact || '—'}</td>
                                                {hasEditPermission && (
                                                    <td className="px-5 py-3.5 text-right">
                                                        {canEditStudent(student, student.schoolId) && (
                                                            <button
                                                                onClick={() => setEditingStudent({
                                                                    registrationId: student.registrationId,
                                                                    studentIndex: student.originalIndex,
                                                                    student: { ...student }
                                                                })}
                                                                className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors ml-auto"
                                                            >
                                                                <Edit2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="md:hidden divide-y divide-white/[0.04]">
                                {students.map((student, idx) => (
                                    <div key={`${student.registrationId}-${idx}`} className="p-4 space-y-2">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs text-white/30 font-mono">#{idx + 1}</span>
                                                    <p className="text-white font-medium">{student.name}</p>
                                                </div>
                                                <p className="text-sm text-white/40">
                                                    {student.schoolName}
                                                    {schools[student.schoolId]?.team_code && (
                                                        <span className="text-white/20 ml-1 font-mono text-xs">
                                                            ({schools[student.schoolId]?.team_code})
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-white/30 mt-1">Grade {student.grade}</p>
                                            </div>
                                            {hasEditPermission && canEditStudent(student, student.schoolId) && (
                                                <button
                                                    onClick={() => setEditingStudent({
                                                        registrationId: student.registrationId,
                                                        studentIndex: student.originalIndex,
                                                        student: { ...student }
                                                    })}
                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06]"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Edit Student Modal */}
            <Modal
                isOpen={!!editingStudent}
                onClose={() => {
                    setEditingStudent(null);
                    setEditReason('');
                }}
                title="Edit Participant"
                size="md"
            >
                <div className="space-y-4">
                    <Input
                        label="Full Name"
                        value={editingStudent?.student.name || ''}
                        onChange={(e) => setEditingStudent(prev => prev ? {
                            ...prev,
                            student: { ...prev.student, name: e.target.value }
                        } : null)}
                        fullWidth
                    />
                    <Input
                        label="Grade"
                        value={editingStudent?.student.grade || ''}
                        onChange={(e) => setEditingStudent(prev => prev ? {
                            ...prev,
                            student: { ...prev.student, grade: e.target.value }
                        } : null)}
                        fullWidth
                    />
                    <Input
                        label="Contact"
                        value={editingStudent?.student.contact || ''}
                        onChange={(e) => setEditingStudent(prev => prev ? {
                            ...prev,
                            student: { ...prev.student, contact: e.target.value }
                        } : null)}
                        fullWidth
                    />

                    <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                            Reason for Edit (Required)
                        </label>
                        <textarea
                            className="w-full bg-surface border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm min-h-[100px] focus:border-brand/30 focus:outline-none resize-none placeholder:text-white/25"
                            value={editReason}
                            onChange={(e) => setEditReason(e.target.value)}
                            placeholder="Why are you changing this information?"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.04]">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setEditingStudent(null);
                                setEditReason('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveEdit}
                            isLoading={saving}
                            disabled={!editReason.trim()}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
